import { Grid, FormControl, Typography, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { touchpointsMocks } from 'pages/calculator/MediaPlanning/components/Touchpoints/mocks';
import { TikTokTouchpointStateKeys } from 'pages/calculator/MediaPlanning/components/Touchpoints/TikTokTouchpoint/interfaces';
import MultiSelectAutocomplete from 'pages/calculator/DigitalFairValue/components/MultiSelectAutocomplete';
import { YoutubeTouchPoint } from '../../../../../../../store/digitalFairValueFormStore';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import usePlanStore from '../../../../../../../store/planStore';
import ErrorMessage from '../../../../common/ErrorMessage/ErrorMessage';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../../config/linking';

interface PartnerSelectorsProps {
  partners: { id: string; title: string }[];
  partnerBudgetShare: number;
  fetchedPartnersData?: { partners: string[] };
  handleChangeStateValues: (
    key: TikTokTouchpointStateKeys,
    value: number | string | { id: string; title: string }
  ) => void;
  minMaxSelectorsValues?: YoutubeTouchPoint;
}

function PartnersSelectors({
  partners,
  partnerBudgetShare,
  fetchedPartnersData,
  handleChangeStateValues,
  minMaxSelectorsValues,
}: PartnerSelectorsProps) {
  const { t } = useTranslation();

  const { countryForms, selectedCountry } = usePlanStore();

  const { tiktokConfig } = countryForms[selectedCountry!];
  const isPartnersError =
    tiktokConfig?.error && tiktokConfig?.partners?.length === 0;

  return (
    <Grid container>
      <Grid item xs={3} sx={{ pr: '24px' }}>
        <FormControl fullWidth>
          <MultiSelectAutocomplete
            options={fetchedPartnersData?.partners?.map((partner) => ({
              id: partner,
              title: partner,
            }))}
            id="partnersMultiSelect"
            label={t('mediaPlanning.partners')}
            noOptionsText={t('mediaPlanning.noPartners')}
            onChange={(_: unknown, value: { id: string; title: string }) =>
              handleChangeStateValues('partners', value)
            }
            value={partners}
            limitTags={1}
            disableClearable={false}
            disabled={false}
            error={isPartnersError}
          />
          {isPartnersError && (
            <ErrorMessage message={t('mediaPlanning.errors.partners')} />
          )}
        </FormControl>
      </Grid>
      <Grid
        item
        xs={3}
        sx={{ pr: '24px', display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="caption" lineHeight={1}>
          {`${t(
            'mediaPlanning.partnerBudgetShare'
          )} (${convertDecimalToPercentValue(partnerBudgetShare ?? 0)}%)`}
        </Typography>
        <Slider
          valueLabelDisplay="auto"
          value={+partnerBudgetShare}
          step={0.05}
          min={touchpointsMocks.tiktokSliders.budgetShare.min}
          max={touchpointsMocks.tiktokSliders.budgetShare.max}
          marks={[
            {
              value: touchpointsMocks.tiktokSliders.budgetShare.min,
              label: convertDecimalToPercentValue(
                touchpointsMocks.tiktokSliders.budgetShare.min
              ),
            },
            {
              value: touchpointsMocks.tiktokSliders.budgetShare.max,
              label: convertDecimalToPercentValue(
                touchpointsMocks.tiktokSliders.budgetShare.max
              ),
            },
          ]}
          valueLabelFormat={(value) =>
            `${convertDecimalToPercentValue(value).toFixed(0)}%`
          }
          onChange={(_, newValue) =>
            handleChangeStateValues('partnerBudgetShare', +newValue)
          }
        />
      </Grid>
      <Grid item xs={2}>
        <HelperTooltip tooltipData={linksConfig.partnerShareRecommendation} />
      </Grid>
    </Grid>
  );
}

export default PartnersSelectors;
